<template>
  <div
    ref="timeline"
    class="demand-timeline"
    @mousedown="onMouseDown"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
    @wheel="onWheel"
  >
    <!-- Guide labels (left side) -->
    <div
      v-for="guide in guideLines"
      :key="'guide-label-' + guide.value"
      class="guide-label"
      :style="{ top: guide.px + 'px' }"
    >
      {{ guide.value }}
    </div>

    <svg
      :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
      preserveAspectRatio="none"
      class="timeline-svg"
    >
      <!-- Day separator lines -->
      <line
        v-for="i in 6"
        :key="'sep-' + i"
        :x1="(i * svgWidth) / 7"
        :x2="(i * svgWidth) / 7"
        y1="0"
        :y2="chartHeight"
        stroke="#d1d5db"
        stroke-width="0.5"
      />

      <!-- Filled area under curve -->
      <path
        :d="areaPath"
        fill="rgba(59, 130, 246, 0.12)"
      />

      <!-- Smooth line -->
      <path
        :d="linePath"
        fill="none"
        stroke="rgba(59, 130, 246, 0.5)"
        stroke-width="1.5"
        stroke-linejoin="round"
      />

      <!-- Peak highlight ranges -->
      <rect
        v-for="(range, ri) in peakRanges"
        :key="'peak-' + ri"
        :x="range.x"
        y="0"
        :width="range.width"
        :height="chartHeight"
        fill="rgba(239, 68, 68, 0.08)"
      />

      <!-- Guide lines (mid / max) -->
      <line
        v-for="guide in guideLines"
        :key="'guide-' + guide.value"
        x1="0"
        :x2="svgWidth"
        :y1="guide.y"
        :y2="guide.y"
        stroke="rgba(107, 114, 128, 0.2)"
        stroke-width="0.5"
        stroke-dasharray="4 3"
      />

      <!-- Cursor line -->
      <line
        :x1="cursorX"
        :x2="cursorX"
        y1="0"
        :y2="chartHeight"
        stroke="rgba(239, 68, 68, 0.5)"
        stroke-width="1"
      />


    </svg>

    <!-- Time label above timeline -->
    <div
      v-if="timeLabel"
      class="cursor-label"
      :style="cursorLabelStyle"
    >
      {{ timeLabelTime }}
    </div>

    <!-- Day labels -->
    <div class="day-labels">
      <div
        v-for="(day, idx) in dayLabels"
        :key="idx"
        class="day-label"
        :class="{ 'day-label--active': idx === selectedDay }"
      >
        {{ day }}
      </div>
    </div>
  </div>
</template>

<script>
import { TOTAL_SLOTS, SLOTS_PER_DAY } from '@/store/modules/insights.store'

export default {
  name: 'DemandTimeline',

  props: {
    barHeights: {
      type: Array,
      default: () => new Array(TOTAL_SLOTS).fill(0)
    },
    selectedIndex: {
      type: Number,
      default: 0
    },
    timeLabel: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      isDragging: false,
      dayLabels: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      svgWidth: 700,
      svgHeight: 64,
      chartHeight: 58,
      padding: 2
    }
  },

  computed: {
    maxHeight() {
      return Math.max(...this.barHeights, 1)
    },

    selectedDay() {
      return Math.floor(this.selectedIndex / SLOTS_PER_DAY)
    },

    heightScale() {
      if (this.maxHeight <= 2) {
        return 1 / 3
      }

      if (this.maxHeight <= 5) {
        return 2 / 3
      }

      return 1
    },

    points() {
      const h = (this.chartHeight - this.padding) * this.heightScale
      return this.barHeights.map((val, i) => ({
        x: (i / (TOTAL_SLOTS - 1)) * this.svgWidth,
        y: this.chartHeight - (val / this.maxHeight) * h
      }))
    },

    smoothedPoints() {
      const pts = this.points
      if (pts.length < 3) {
        return pts
      }

      const windowSize = 3
      return pts.map((p, i) => {
        let sumY = 0
        let count = 0
        for (let j = i - windowSize; j <= i + windowSize; j++) {
          if (j >= 0 && j < pts.length) {
            sumY += pts[j].y
            count++
          }
        }

        return { x: p.x, y: sumY / count }
      })
    },

    linePath() {
      const pts = this.smoothedPoints
      if (pts.length < 2) {
        return ''
      }

      return this.catmullRomPath(pts)
    },

    areaPath() {
      const pts = this.smoothedPoints
      if (pts.length < 2) {
        return ''
      }

      const line = this.catmullRomPath(pts)
      const lastX = pts[pts.length - 1].x
      const firstX = pts[0].x
      return `${line} L ${lastX},${this.chartHeight} L ${firstX},${this.chartHeight} Z`
    },

    guideLines() {
      if (this.maxHeight <= 1) {
        return []
      }

      const h = (this.chartHeight - this.padding) * this.heightScale
      const yForValue = (val) => this.chartHeight - (val / this.maxHeight) * h
      const mid = Math.round(this.maxHeight / 2)
      const px = (val) => (yForValue(val) / this.svgHeight) * 64
      const lines = [
        { value: this.maxHeight, y: yForValue(this.maxHeight), px: px(this.maxHeight) }
      ]

      if (mid > 0 && mid < this.maxHeight) {
        lines.push({ value: mid, y: yForValue(mid), px: px(mid) })
      }

      return lines
    },

    peakRanges() {
      if (this.maxHeight <= 1) {
        return []
      }

      const slotWidth = this.svgWidth / (TOTAL_SLOTS - 1)
      const ranges = []
      let start = null
      for (let i = 0; i < TOTAL_SLOTS; i++) {
        if (this.barHeights[i] === this.maxHeight) {
          if (start === null) {
            start = i
          }
        } else if (start !== null) {
          ranges.push({
            x: start * slotWidth,
            width: (i - start) * slotWidth
          })
          start = null
        }
      }

      if (start !== null) {
        ranges.push({
          x: start * slotWidth,
          width: (TOTAL_SLOTS - start) * slotWidth
        })
      }

      return ranges
    },

    cursorX() {
      return (this.selectedIndex / (TOTAL_SLOTS - 1)) * this.svgWidth
    },

    cursorPercent() {
      return (this.selectedIndex / (TOTAL_SLOTS - 1)) * 100
    },

    timeLabelDay() {
      if (!this.timeLabel) {
        return ''
      }

      const parts = this.timeLabel.split(' ')
      return parts[0] || ''
    },

    timeLabelTime() {
      if (!this.timeLabel) {
        return ''
      }

      const parts = this.timeLabel.split(' ')
      return parts.slice(1).join(' ') || ''
    },

    cursorLabelStyle() {
      return {
        left: this.cursorPercent + '%'
      }
    },

  },

  mounted() {
    this._onMouseUp = () => {
      this.isDragging = false 
    }

    document.addEventListener('mouseup', this._onMouseUp)
  },

  beforeDestroy() {
    document.removeEventListener('mouseup', this._onMouseUp)
  },

  methods: {
    catmullRomPath(pts, tension = 0.3) {
      if (pts.length < 2) {
        return ''
      }

      let d = `M ${pts[0].x},${pts[0].y}`

      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(i - 1, 0)]
        const p1 = pts[i]
        const p2 = pts[i + 1]
        const p3 = pts[Math.min(i + 2, pts.length - 1)]

        const cp1x = p1.x + (p2.x - p0.x) * tension / 3
        const cp1y = p1.y + (p2.y - p0.y) * tension / 3
        const cp2x = p2.x - (p3.x - p1.x) * tension / 3
        const cp2y = p2.y - (p3.y - p1.y) * tension / 3

        d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`
      }

      return d
    },

    selectSlot(idx) {
      const clamped = Math.max(0, Math.min(TOTAL_SLOTS - 1, idx))
      this.$emit('update:selectedIndex', clamped)
    },

    onWheel(e) {
      e.preventDefault()
      const delta = e.deltaY !== 0
        ? (e.deltaY > 0 ? 1 : -1)
        : (e.deltaX > 0 ? 1 : -1)
      this.selectSlot(this.selectedIndex + delta)
    },

    onMouseDown(e) {
      this.isDragging = true
      this.selectFromMouseEvent(e)
    },

    onMouseMove(e) {
      if (!this.isDragging) {
        return
      }

      this.selectFromMouseEvent(e)
    },

    onMouseLeave() {
      this.isDragging = false
    },

    selectFromMouseEvent(e) {
      const el = this.$refs.timeline
      if (!el) {
        return
      }

      const rect = el.getBoundingClientRect()
      const x = e.clientX - rect.left
      const ratio = x / rect.width
      const idx = Math.round(ratio * (TOTAL_SLOTS - 1))
      this.selectSlot(idx)
    }
  }
}
</script>

<style scoped>
.demand-timeline {
  position: relative;
  width: 100%;
  user-select: none;
  cursor: pointer;
}

.timeline-svg {
  width: 100%;
  height: 64px;
  display: block;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px 4px 0 0;
}

.day-labels {
  display: flex;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 4px 4px;
}

.day-label {
  flex: 1;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  padding: 2px 0;
}

.day-label--active {
  color: #2563eb;
  background: rgba(59, 130, 246, 0.08);
}

.guide-label {
  position: absolute;
  left: -14px;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 600;
  color: rgba(107, 114, 128, 0.5);
  pointer-events: none;
}

.cursor-label {
  position: absolute;
  top: -2px;
  display: flex;
  font-size: 80%;
  font-weight: 700;
  white-space: nowrap;
  pointer-events: none;
  color: rgba(107, 114, 128, 0.5);
  transform: translateX(-50%);
}
</style>
